/**
 * Windows (özellikle Chrome) bölgesel gösterge emoji çiftlerini bayrak olarak
 * çizmez; "🇺🇸" yerine "US" harfleri görünür. flagcdn görseli tüm tarayıcılarda
 * aynıdır.
 */

import { MARKET_PLACE } from '@sellsen/contants';

const REGIONAL_INDICATOR_A = 0x1f1e6;

const CODE_ALIASES: Record<string, string> = {
  UK: 'GB',
};

export type CountryFlagProps = {
  /** ISO 3166-1 alpha-2 (veya EU). */
  countryCode?: string;
  /** API'den gelen bayrak emojisi; countryCode yoksa buradan ISO kodu türetilir. */
  emoji?: string;
  /** UNITED_STATES gibi marketplace id; ISO kod yoksa MARKET_PLACE'ten çözülür. */
  marketplace?: string;
  size?: number;
  title?: string;
  className?: string;
};

export function emojiFlagToIsoCode(
  emoji: string | undefined
): string | undefined {
  if (!emoji) return undefined;
  const chars = Array.from(emoji.trim());
  if (chars.length < 2) return undefined;
  const first = chars[0].codePointAt(0);
  const second = chars[1].codePointAt(0);
  if (first == null || second == null) return undefined;
  if (
    first < REGIONAL_INDICATOR_A ||
    first > REGIONAL_INDICATOR_A + 25 ||
    second < REGIONAL_INDICATOR_A ||
    second > REGIONAL_INDICATOR_A + 25
  ) {
    return undefined;
  }
  return (
    String.fromCharCode(first - REGIONAL_INDICATOR_A + 65) +
    String.fromCharCode(second - REGIONAL_INDICATOR_A + 65)
  );
}

function normalizeIsoCode(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!/^[A-Za-z]{2}$/.test(trimmed)) return undefined;
  const upper = trimmed.toUpperCase();
  return CODE_ALIASES[upper] ?? upper;
}

function fromMarketplaceId(id: string | undefined): string | undefined {
  if (!id) return undefined;
  return MARKET_PLACE.find((m) => m.id === id || m.name === id)?.countryCode;
}

export function resolveFlagIsoCode({
  countryCode,
  emoji,
  marketplace,
}: Pick<CountryFlagProps, 'countryCode' | 'emoji' | 'marketplace'>):
  | string
  | undefined {
  return (
    normalizeIsoCode(countryCode) ??
    fromMarketplaceId(countryCode) ??
    fromMarketplaceId(marketplace) ??
    emojiFlagToIsoCode(emoji)
  );
}

export function flagCdnUrl(isoCode: string): string {
  return `https://flagcdn.com/${isoCode.toLowerCase()}.svg`;
}

export const CountryFlag = ({
  countryCode,
  emoji,
  marketplace,
  size = 20,
  title,
  className,
}: CountryFlagProps) => {
  const iso = resolveFlagIsoCode({ countryCode, emoji, marketplace });
  if (!iso) return null;

  const pixelHeight = Math.max(12, Math.round(size));

  return (
    <img
      src={flagCdnUrl(iso)}
      alt=""
      title={title}
      aria-hidden
      className={className}
      style={{
        width: Math.round(pixelHeight * (4 / 3)),
        height: pixelHeight,
        objectFit: 'cover',
        borderRadius: 2,
        display: 'inline-block',
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    />
  );
};
